import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { invalidateFormNosCache } from "../utils/formNos";

const API = "https://eps.elkomprintama.com/";

const FORM_KEY_OPTIONS = [
  { value: "", label: "— Unassigned —" },
  { value: "MATERIAL_REQUEST", label: "Material Request (F-QP-STR)" },
  { value: "QC_TRANSFER", label: "QC Transfer to Store (F-QP-QC-01)" },
  { value: "FINISHED_GOODS", label: "Finished Goods (F-QP-QC-02)" },
  { value: "QUOTATION", label: "Quotation (F-QP-MKT)" },
];

const KEY_BADGE = {
  MATERIAL_REQUEST: "bg-pink-100 text-pink-700",
  QC_TRANSFER: "bg-teal-100 text-teal-700",
  FINISHED_GOODS: "bg-purple-100 text-purple-700",
  QUOTATION: "bg-orange-100 text-orange-700",
};

const KEY_LABEL = {
  MATERIAL_REQUEST: "Material Request",
  QC_TRANSFER: "QC Transfer to Store",
  FINISHED_GOODS: "Finished Goods",
  QUOTATION: "Quotation",
};

function FormNumbers() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ form_num: "", form_key: "" });
  const [adding, setAdding] = useState(false);
  const [newValues, setNewValues] = useState({ form_num: "", form_key: "" });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}get/api/formno`, {
        withCredentials: true,
      });
      setRecords(res.data.result || []);
    } catch {
      toast.error("Failed to load form numbers.");
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.form_id);
    setEditValues({ form_num: row.form_num, form_key: row.form_key || "" });
    setAdding(false);
  };

  const handleSave = async () => {
    if (!editValues.form_num.trim()) {
      toast.warning("Form number cannot be empty.");
      return;
    }
    try {
      await axios.patch(`${API}patch/api/formno/${editingId}`, editValues, {
        withCredentials: true,
      });
      toast.success("Updated!");
      invalidateFormNosCache();
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update.");
    }
  };

  const handleAdd = async () => {
    if (!newValues.form_num.trim()) {
      toast.warning("Form number cannot be empty.");
      return;
    }
    try {
      await axios.post(`${API}post/api/formno`, newValues, {
        withCredentials: true,
      });
      toast.success("Added!");
      invalidateFormNosCache();
      setAdding(false);
      setNewValues({ form_num: "", form_key: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to add.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this form number?")) return;
    try {
      await axios.delete(`${API}delete/api/formno/${id}`, {
        withCredentials: true,
      });
      toast.success("Deleted.");
      invalidateFormNosCache();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to delete.");
    }
  };

  const inp =
    "border border-gray-300 rounded px-3 py-1.5 text-xs focus:outline-none focus:border-indigo-400 bg-white w-full";
  const sel =
    "border border-gray-300 rounded px-3 py-1.5 text-xs focus:outline-none focus:border-indigo-400 bg-white w-full";

  return (
    <>
      <div className="w-full rounded-lg bg-indigo-600 mb-4">
        <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
          Form Numbers
        </label>
      </div>

      <div className="bg-gray-100 shadow-md px-5 py-5 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-500">
            Manage form numbers printed on official documents. Assign each
            record to a form to link it automatically.
          </p>
          <button
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
          >
            + Add Form Number
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-indigo-700 text-white uppercase text-left">
              <tr>
                <th className="px-4 py-3 border-r w-12">ID</th>
                <th className="px-4 py-3 border-r">Form Number</th>
                <th className="px-4 py-3 border-r">Assigned To</th>
                <th className="px-4 py-3 text-center w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Add row */}
              {adding && (
                <tr className="bg-indigo-50 border-b">
                  <td className="px-4 py-3 border-r text-gray-400 italic">
                    new
                  </td>
                  <td className="px-4 py-3 border-r">
                    <input
                      className={inp}
                      placeholder="e.g. F-QP-STR-01-01 Rev.03"
                      autoFocus
                      value={newValues.form_num}
                      onChange={(e) =>
                        setNewValues((v) => ({
                          ...v,
                          form_num: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td className="px-4 py-3 border-r">
                    <select
                      className={sel}
                      value={newValues.form_key}
                      onChange={(e) =>
                        setNewValues((v) => ({
                          ...v,
                          form_key: e.target.value,
                        }))
                      }
                    >
                      {FORM_KEY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={handleAdd}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-2.5 py-1 rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setAdding(false)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium px-2.5 py-1 rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {isLoading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-10 text-gray-400 italic"
                  >
                    Loading…
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-10 text-gray-400 italic"
                  >
                    No records yet
                  </td>
                </tr>
              ) : (
                records.map((row, i) => {
                  const isEditing = editingId === row.form_id;
                  return (
                    <tr
                      key={row.form_id}
                      className={`border-b hover:bg-indigo-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    >
                      <td className="px-4 py-2.5 border-r text-gray-400">
                        {row.form_id}
                      </td>

                      {/* Form number cell */}
                      <td className="px-4 py-2.5 border-r font-mono font-semibold text-gray-800">
                        {isEditing ? (
                          <input
                            className={inp}
                            autoFocus
                            value={editValues.form_num}
                            onChange={(e) =>
                              setEditValues((v) => ({
                                ...v,
                                form_num: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          row.form_num
                        )}
                      </td>

                      {/* Assigned To cell */}
                      <td className="px-4 py-2.5 border-r">
                        {isEditing ? (
                          <select
                            className={sel}
                            value={editValues.form_key}
                            onChange={(e) =>
                              setEditValues((v) => ({
                                ...v,
                                form_key: e.target.value,
                              }))
                            }
                          >
                            {FORM_KEY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : row.form_key ? (
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${KEY_BADGE[row.form_key] || "bg-gray-100 text-gray-600"}`}
                          >
                            {KEY_LABEL[row.form_key] || row.form_key}
                          </span>
                        ) : (
                          <span className="text-gray-300 italic">
                            — unassigned —
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2.5 text-center">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={handleSave}
                              className="bg-green-600 hover:bg-green-700 text-white font-bold px-2.5 py-1 rounded text-xs"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium px-2.5 py-1 rounded text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => startEdit(row)}
                              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-2.5 py-1 rounded text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(row.form_id)}
                              className="bg-red-100 hover:bg-red-200 text-red-600 font-bold px-2.5 py-1 rounded text-xs"
                            >
                              Del
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Key legend */}
        <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Form Key Reference
          </p>
          <div className="flex flex-wrap gap-2">
            {FORM_KEY_OPTIONS.filter((o) => o.value).map((o) => (
              <span
                key={o.value}
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${KEY_BADGE[o.value]}`}
              >
                {o.value} → {o.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default FormNumbers;
